import React, { useEffect, useState, useCallback } from 'react';
import { Home, Briefcase, MapPin, Star, Trash2, Plus, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconForLabel = (label) => {
  const l = (label || '').toLowerCase();
  if (l.includes('casa') || l.includes('home')) return <Home size={14} />;
  if (l.includes('trab') || l.includes('work')) return <Briefcase size={14} />;
  return <MapPin size={14} />;
};

/**
 * Saved Addresses block for the checkout location step.
 *
 * Lets the customer:
 *  - Pick from previously saved addresses with one tap (chips at the top)
 *  - Save the currently filled-in location as a new favourite
 *
 * Props:
 *  - token: JWT (optional — for cookie-only Google OAuth users we still send credentials:'include')
 *  - currentLocation: object with delivery_location/location_type/hotel_name/room_number/gps_coordinates
 *  - onPick: callback receiving a SavedAddress object when user taps a chip
 *  - language: 'pt' | 'en'
 */
export const SavedAddressesBlock = ({ token, currentLocation, onPick, language = 'pt' }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [newLabel, setNewLabel] = useState('');

  const headers = () => {
    const h = { 'Content-Type': 'application/json' };
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally omits unused `headers` helper closure
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const h = { 'Content-Type': 'application/json' };
      if (token) h.Authorization = `Bearer ${token}`;
      const r = await fetch(`${API_URL}/api/me/addresses`, {
        headers: h,
        credentials: 'include',
      });
      if (r.ok) setItems(await r.json());
    } catch (e) { console.debug('saved-addresses load failed', e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const canSave =
    !!(currentLocation?.delivery_location && currentLocation.delivery_location.length > 0);

  const handleSave = async (label) => {
    const useLabel = (label || newLabel || '').trim();
    if (!useLabel) {
      toast.error(language === 'pt' ? 'Indique um nome (Casa, Trabalho...)' : 'Enter a label');
      return;
    }
    if (!canSave) {
      toast.error(language === 'pt' ? 'Preencha o endereço primeiro' : 'Fill the address first');
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/me/addresses`, {
        method: 'POST',
        headers: headers(),
        credentials: 'include',
        body: JSON.stringify({
          label: useLabel,
          delivery_location: currentLocation.delivery_location,
          location_type: currentLocation.location_type || 'other',
          hotel_name: currentLocation.hotel_name || null,
          room_number: currentLocation.room_number || null,
          gps_coordinates: currentLocation.gps_coordinates || '',
          is_default: items.length === 0,
        }),
      });
      if (r.ok) {
        toast.success(language === 'pt' ? 'Morada guardada' : 'Address saved');
        setShowSave(false);
        setNewLabel('');
        await load();
      } else {
        const j = await r.json().catch(() => ({}));
        toast.error(j.detail || (language === 'pt' ? 'Erro ao guardar' : 'Save error'));
      }
    } catch (e) {
      toast.error(language === 'pt' ? 'Erro de rede' : 'Network error');
    }
    setSaving(false);
  };

  const handleDelete = async (addressId) => {
    try {
      const r = await fetch(`${API_URL}/api/me/addresses/${addressId}`, {
        method: 'DELETE',
        headers: headers(),
        credentials: 'include',
      });
      if (r.ok) {
        setItems((prev) => prev.filter((a) => a.address_id !== addressId));
      }
    } catch (e) { /* silent */ }
  };

  if (loading) return null;

  return (
    <div className="space-y-3" data-testid="saved-addresses-block">
      {/* Saved chips */}
      {items.length > 0 && (
        <div>
          <div className="text-xs text-[#9FA2B4] mb-2 font-medium uppercase tracking-wider">
            {language === 'pt' ? 'Moradas guardadas' : 'Saved addresses'}
          </div>
          <div className="flex flex-wrap gap-2">
            {items.map((a) => (
              <div
                key={a.address_id}
                className="group inline-flex items-center gap-1 bg-white border border-[#2EC4B6]/40 hover:border-[#2EC4B6] rounded-full pl-3 pr-1 py-1 transition-colors"
              >
                <button
                  type="button"
                  onClick={() => onPick?.(a)}
                  className="flex items-center gap-1.5 text-sm text-[#004E64] font-medium"
                  data-testid={`saved-address-${a.address_id}`}
                >
                  {iconForLabel(a.label)}
                  <span>{a.label}</span>
                  {a.is_default && <Star size={10} className="text-[#FF6B35] fill-[#FF6B35]" />}
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleDelete(a.address_id); }}
                  className="p-1 rounded-full hover:bg-red-50 text-[#9FA2B4] hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="delete"
                  data-testid={`delete-address-${a.address_id}`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Save current as favourite */}
      {canSave && !showSave && items.length < 10 && (
        <button
          type="button"
          onClick={() => setShowSave(true)}
          className="text-xs text-[#FF6B35] hover:underline flex items-center gap-1 font-medium"
          data-testid="open-save-address"
        >
          <Plus size={12} />
          {language === 'pt' ? 'Guardar esta morada' : 'Save this address'}
        </button>
      )}

      {showSave && (
        <div className="bg-[#FFF7ED] border border-[#FF6B35]/30 rounded-xl p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-[#004E64]">
              {language === 'pt' ? 'Guardar como:' : 'Save as:'}
            </span>
            <button
              type="button"
              onClick={() => { setShowSave(false); setNewLabel(''); }}
              className="text-[#9FA2B4] hover:text-[#004E64]"
            >
              <X size={16} />
            </button>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-full"
              disabled={saving}
              onClick={() => handleSave('Casa')}
              data-testid="quick-save-home"
            >
              <Home size={12} className="mr-1" />
              {language === 'pt' ? 'Casa' : 'Home'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-full"
              disabled={saving}
              onClick={() => handleSave('Trabalho')}
              data-testid="quick-save-work"
            >
              <Briefcase size={12} className="mr-1" />
              {language === 'pt' ? 'Trabalho' : 'Work'}
            </Button>
          </div>
          <div className="flex gap-2">
            <Input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder={language === 'pt' ? 'Outro nome (ex: Hotel, Praia)' : 'Other name (ex: Hotel)'}
              className="rounded-full text-sm h-9"
              data-testid="custom-label-input"
              maxLength={20}
            />
            <Button
              type="button"
              size="sm"
              className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full"
              disabled={saving || !newLabel.trim()}
              onClick={() => handleSave()}
              data-testid="custom-label-save"
            >
              {language === 'pt' ? 'Guardar' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedAddressesBlock;
